import React, { useState, useEffect } from 'react';
import { staffAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import StaffForm from './StaffForm';
import toast from 'react-hot-toast';
import { getInitials } from '../../utils/helpers';

const StaffList = () => {
  const [staff, setStaff] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ staff_type: '', status: '' });
  const [departments, setDepartments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editStaff, setEditStaff] = useState(null);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 15, search, ...filter };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await staffAPI.list(params);
      setStaff(res.data.results || res.data);
      setCount(res.data.count || 0);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStaff(); }, [page, search, filter]);
  useEffect(() => {
    staffAPI.departments().then(r => setDepartments(r.data.results || r.data)).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try { await staffAPI.delete(id); toast.success('Deleted'); fetchStaff(); } catch { toast.error('Failed'); }
  };

  const columns = [
    { key: 'photo', label: '', render: (photo, row) => (
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#1a3a5c,#0ea5e9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
        {getInitials(row.full_name || `${row.first_name} ${row.last_name}`)}
      </div>
    )},
    { key: 'employee_id', label: 'Emp. ID', render: v => <code style={{ fontSize: 12 }}>{v}</code> },
    { key: 'full_name', label: 'Name', render: (v, row) => <strong>{v || `${row.first_name} ${row.last_name}`}</strong> },
    { key: 'designation', label: 'Designation' },
    { key: 'department_name', label: 'Department', render: v => v || '—' },
    { key: 'staff_type', label: 'Type', render: v => <span className="badge bg-info text-dark">{v}</span> },
    { key: 'phone', label: 'Phone' },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
    { key: 'id', label: 'Actions', render: (id, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-outline-primary" onClick={e => { e.stopPropagation(); setEditStaff(row); setShowModal(true); }}>✏️</button>
        <button className="btn btn-sm btn-outline-danger" onClick={e => { e.stopPropagation(); handleDelete(id); }}>🗑️</button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <div><h2>Staff Management</h2> </div>
        <button className="btn btn-primary" onClick={() => { setEditStaff(null); setShowModal(true); }}>➕ Add Staff</button>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-5"><SearchBar onSearch={setSearch} placeholder="Search by name, ID, phone..." /></div>
            <div className="col-md-2">
              <select className="form-select" value={filter.staff_type} onChange={e => setFilter({ ...filter, staff_type: e.target.value })}>
                <option value="">All Types</option>
                <option value="teacher">Teacher</option>
                <option value="admin">Admin</option>
                <option value="support">Support</option>
                <option value="management">Management</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="on_leave">On Leave</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" onClick={() => setFilter({ staff_type: '', status: '' })}>🔄 Reset</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span>👨‍💼 Staff List ({count} total)</span></div>
        <DataTable columns={columns} data={staff} loading={loading} />
        <div className="card-body pt-0">
          <Pagination count={count} page={page} pageSize={15} onChange={setPage} />
        </div>
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)} title={editStaff ? 'Edit Staff' : 'Add New Staff'} size="xl">
        <StaffForm
          staff={editStaff} departments={departments}
          onSave={() => { setShowModal(false); fetchStaff(); }}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
    </div>
  );
};

export default StaffList;
